'use client';

import { useInfiniteQuery } from '@tanstack/react-query';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { postsQuery, type PostFilter } from '@eliadmin/core/src/components/dlz/blog/blog-queries';
import { PostCard } from '@eliadmin/core/src/components/dlz/blog/post-card';
import { toPostPage, type PostPage } from '@eliadmin/core/src/components/dlz/blog/post-card-data';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const IMAGE_SIZES =
  '(min-width: 1392px) 427px, (min-width: 1024px) 31vw, (min-width: 768px) 50vw, 100vw';

/**
 * Figma „Articles grid“ 60343:25290: three columns (24 / 40 px gaps), one column with 24 px gaps on
 * phones (60459:108769). The server renders the first page; „Zobraziť viac článkov“ appends the next
 * one from the same query (secondary button, 48 px full width on phones).
 */
export function PostGrid({
  locale,
  filter,
  initial,
  readMore,
}: {
  locale: string;
  filter: PostFilter;
  initial: PostPage;
  readMore: string;
}) {
  const { t } = useTranslations();
  const query = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, locale, 'dlz-post-grid', filter],
    queryFn: async ({ pageParam }) =>
      toPostPage(await fetchBlogs({ locale, query: postsQuery(filter, { page: pageParam }) })),
    initialPageParam: 1,
    initialData: { pages: [initial], pageParams: [1] },
    getNextPageParam: last => (last.page < last.lastPage ? last.page + 1 : undefined),
    // The server page is fresh; only „load more“ fetches.
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
  // An article published between two requests shifts the pages; list every article once.
  const posts = query.data.pages
    .flatMap(page => page.posts)
    .filter((post, index, all) => all.findIndex(other => other.id === post.id) === index);

  if (!posts.length) {
    return <p className="dlz-body-m py-12 text-center text-[#4F545C]">{t('text554')}</p>;
  }

  return (
    <div className="flex flex-col gap-6 lg:gap-10">
      <ul
        aria-busy={query.isFetchingNextPage}
        className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-6 lg:gap-y-10"
      >
        {posts.map(post => (
          <li key={post.id} className="flex">
            <PostCard
              post={post}
              locale={locale}
              readMore={readMore}
              heading="h2"
              imageSizes={IMAGE_SIZES}
              className="w-full"
            />
          </li>
        ))}
      </ul>
      {query.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            isLoading={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
            className="h-12 w-full md:h-[52px] md:w-auto"
          >
            {t('text588')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
